/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
*/
import isNumber from 'lodash.isnumber';
import isString from 'lodash.isstring';
import pickBy from 'lodash.pickby';
import {getAccountId} from '@ion/applications/src/helpers';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '@ion/applications/src/prompts/constants';
import {error} from '@ion/core/src/logger/logger';
import {
  createDeviceConfiguration,
  deleteDeviceConfiguration,
  getDeviceConfiguration,
  loadDeviceConfigurations,
  updateDeviceConfiguration
} from '@ion/core/src/services/Connect';
import {
  AuditChange,
  DeviceConfigsFilters,
  DeviceConfiguration,
  Entity
} from '@ion/core/src/types/entities';
import {
  LoadDeviceConfigsParams,
  ServiceError
} from '@ion/core/src/types/services';
import {retryUpdate} from '@ion/services/src/helpers/retry';
import {PageInitializeAction} from '@/types/redux';
import {toggleUserPrompt} from './user-prompt';

// Defines the name of the page label's reducer
export const REDUCER_NAME = 'deviceConfigs';

export const INITIALIZE_ADMIN_DEVICE_CONFIGS               = 'INITIALIZE_ADMIN_DEVICE_CONFIGS';
export const SET_ADMIN_DEVICE_CONFIGS_GRID_COLUMNS         = 'SET_ADMIN_DEVICE_CONFIGS_GRID_COLUMNS';
export const UPDATE_ADMIN_DEVICE_CONFIGS_GRID_COLUMNS      = 'UPDATE_ADMIN_DEVICE_CONFIGS_GRID_COLUMNS';
export const ADMIN_DEVICE_CONFIGS_GRID_REFRESHED           = 'ADMIN_DEVICE_CONFIGS_GRID_REFRESHED';
export const SET_GRID_PAGE_SIZE                            = 'SET_DEVICE_CONFIGS_GRID_PAGE_SIZE';
export const GET_ADMIN_DEVICE_CONFIGS_REQUEST              = 'GET_ADMIN_DEVICE_CONFIGS_REQUEST';
export const GET_ADMIN_DEVICE_CONFIGS_RESPONSE             = 'GET_ADMIN_DEVICE_CONFIGS_RESPONSE';
export const GET_ADMIN_DEVICE_CONFIGS_ERROR                = 'GET_ADMIN_DEVICE_CONFIGS_ERROR';
export const DISPLAY_DELETE_DEVICE_CONFIG_DIALOG           = 'DISPLAY_DELETE_DEVICE_CONFIG_DIALOG';
export const DELETE_ADMIN_DEVICE_CONFIG_REQUEST            = 'DELETE_ADMIN_DEVICE_CONFIG_REQUEST';
export const DELETE_ADMIN_DEVICE_CONFIG_RESET              = 'DELETE_ADMIN_DEVICE_CONFIG_RESET';
export const DELETE_ADMIN_DEVICE_CONFIG_RESPONSE           = 'DELETE_ADMIN_DEVICE_CONFIG_RESPONSE';
export const DELETE_ADMIN_DEVICE_CONFIG_ERROR              = 'DELETE_ADMIN_DEVICE_CONFIG_ERROR';
export const ADD_ADMIN_DEVICE_CONFIG_REQUEST               = 'ADD_ADMIN_DEVICE_CONFIG_REQUEST';
export const ADD_ADMIN_DEVICE_CONFIG_RESPONSE              = 'ADD_ADMIN_DEVICE_CONFIG_RESPONSE';
export const ADD_ADMIN_DEVICE_CONFIG_ERROR                 = 'ADD_ADMIN_DEVICE_CONFIG_ERROR';
export const UPDATE_DEVICE_CONFIGS_SEARCH_QUERIES          = 'UPDATE_DEVICE_CONFIGS_SEARCH_QUERIES';
export const RESET_DEVICE_CONFIGS_SEARCH_QUERIES           = 'RESET_DEVICE_CONFIGS_SEARCH_QUERIES';
export const SHOW_ADD_DEVICE_CONFIG_DIALOG                 = 'SHOW_ADD_DEVICE_CONFIG_DIALOG';
export const SHOW_EDIT_DEVICE_CONFIG_DIALOG                = 'SHOW_EDIT_DEVICE_CONFIG_DIALOG';
export const EDIT_ADMIN_DEVICE_CONFIG_REQUEST              = 'EDIT_ADMIN_DEVICE_CONFIG_REQUEST';
export const EDIT_ADMIN_DEVICE_CONFIG_RESPONSE             = 'EDIT_ADMIN_DEVICE_CONFIG_RESPONSE';
export const EDIT_ADMIN_DEVICE_CONFIG_ERROR                = 'EDIT_ADMIN_DEVICE_CONFIG_ERROR';
export const AUDIT_ADMIN_DEVICE_CONFIG                     = 'AUDIT_ADMIN_DEVICE_CONFIG';
export const AUDIT_ADMIN_DEVICE_CONFIG_REQUEST             = 'AUDIT_ADMIN_DEVICE_CONFIG_REQUEST';
export const AUDIT_ADMIN_DEVICE_CONFIG_RESPONSE            = 'AUDIT_ADMIN_DEVICE_CONFIG_RESPONSE';
export const AUDIT_ADMIN_DEVICE_CONFIG_ERROR               = 'AUDIT_ADMIN_DEVICE_CONFIG_ERROR';
export const AUDIT_ADMIN_DEVICE_CONFIG_SHOW_CHANGES_DIALOG = 'AUDIT_ADMIN_DEVICE_CONFIG_SHOW_CHANGES_DIALOG';

// Dispatches an action to perform redux initialization for Device Configs
export const initializeAdminDeviceConfigs = (): PageInitializeAction => ({
  type : INITIALIZE_ADMIN_DEVICE_CONFIGS
});

// Dispatches an action when the Device Configs grid has been refreshed
export const adminDeviceConfigsGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_DEVICE_CONFIGS_GRID_REFRESHED};
};

// Updates the modular search queries for Device Configs
export const updateDeviceConfigsSearchQueries = (searchParams: DeviceConfigsFilters) => ({
  type : UPDATE_DEVICE_CONFIGS_SEARCH_QUERIES, searchParams
});

// Resets the Device Configs filters state
export const resetDeviceConfigsSearchQueries = () => ({
  type : RESET_DEVICE_CONFIGS_SEARCH_QUERIES
});

// Dispatches actions related to retrieving Device Configs from services
export const getAdminDeviceConfigs = ({
  sort,
  callback,
  pageId = 1,
  pageSize = 20
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_DEVICE_CONFIGS_REQUEST});
    const {
      deviceConfigs: {
        search
      }
    } = getState();
    const endpointOptions: LoadDeviceConfigsParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize
    };
    if (sort) {
      endpointOptions.sortOn = sort;
    }
    const availableStringSearchAttributes = pickBy(search, isString);
    const availableNumberSearchAttributes = pickBy(search, isNumber);
    endpointOptions.filterOn = {
      ...availableStringSearchAttributes,
      ...availableNumberSearchAttributes
    };
    try {
      const {deviceConfigurations: items, totalCount} = await loadDeviceConfigurations(endpointOptions);
      dispatch({type: GET_ADMIN_DEVICE_CONFIGS_RESPONSE});
      callback?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callback({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_DEVICE_CONFIGS_ERROR, error: err});
      return err;
    }
  };
};

// Dispatches an action to show or hide the Device Config deletion confirmation dialog to the user
export const displayDeleteDialog = (deviceConfig: DeviceConfiguration, show = true) => ({
  type : DISPLAY_DELETE_DEVICE_CONFIG_DIALOG, deviceConfig, show
});

// Dispatches actions related to deleting a single Device Config
export const deleteAdminDeviceConfig = (deviceConfig: DeviceConfiguration) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_DEVICE_CONFIG_REQUEST});
    try {
      const deleted = await deleteDeviceConfiguration(deviceConfig);
      dispatch({type: DELETE_ADMIN_DEVICE_CONFIG_RESPONSE, deviceConfig: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_DEVICE_CONFIG_ERROR, error: err});
      error(err);
    }
  };
};

// Opens or closes the Add Device Config dialog
export const showAddDeviceConfigDialog = (showDialog: boolean = true) => ({
  type : SHOW_ADD_DEVICE_CONFIG_DIALOG, showDialog
});

// Dispatches actions related to creating the given Device Config
export const addDeviceConfig = (deviceConfig: DeviceConfiguration) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_ADMIN_DEVICE_CONFIG_REQUEST});
    try {
      const added = await createDeviceConfiguration(deviceConfig);
      dispatch({type: ADD_ADMIN_DEVICE_CONFIG_RESPONSE, deviceConfig: added});
      dispatch(toggleUserPrompt(successAddOptions(Entity.deviceconfiguration)));
    } catch (err) {
      dispatch({type: ADD_ADMIN_DEVICE_CONFIG_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.deviceconfiguration, err)));
      error(err);
    }
  };
};

// Shows or hides the Edit Device Config dialog
export const showEditDeviceConfigDialog = (
  deviceConfig: DeviceConfiguration,
  showDialog: boolean = true,
  isView: boolean = false
) => ({
  type : SHOW_EDIT_DEVICE_CONFIG_DIALOG, deviceConfig, showDialog, isView
});

// Handles the dispatching of actions related to editing a Device Config
export const editDeviceConfig = (deviceConfig: DeviceConfiguration) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_DEVICE_CONFIG_REQUEST});
    try {
      const updated = await retryUpdate({
        newEntity    : deviceConfig,
        updateEntity : updateDeviceConfiguration,
        getEntity    : getSingleDeviceConfig,
        onError      : deviceConfigUpdateError,
        dispatch
      });
      if (updated) {
        dispatch({type: EDIT_ADMIN_DEVICE_CONFIG_RESPONSE, deviceConfig: updated});
        dispatch(toggleUserPrompt(successEditOptions(Entity.deviceconfiguration)));
      }
    } catch (err) {
      deviceConfigUpdateError(err, dispatch);
    }
  };
};

/**
 * Invoked when an existing Device Config gets resource out of state, delegating to the fetch
 * latest device config details successful response or error action accordingly.
 */
const getSingleDeviceConfig = async(deviceConfigID: number) => {
  try {
    const deviceConfig = await getDeviceConfiguration(deviceConfigID);
    return {updated: deviceConfig, error: ''};
  } catch (err) {
    error(err);
    return {updated: null, error: err};
  }
};

/**
 * Invoked when an existing Device Config gets updated or fetched
 * and dispatching a successful or error action accordingly.
 */
const deviceConfigUpdateError = (err: ServiceError, dispatch: Function) => {
  dispatch({type: EDIT_ADMIN_DEVICE_CONFIG_ERROR, error: err});
  dispatch(toggleUserPrompt(errorEditOptions(Entity.deviceconfiguration, err)));
  error(err);
};

// Dispatches the action for viewing the Audit Changes for a Device Config
export const auditAdminDeviceConfig = (deviceConfig: DeviceConfiguration, show: boolean = true) => ({
  type : AUDIT_ADMIN_DEVICE_CONFIG, deviceConfig, show
});

// Shows or hides the Audit Device Config changes dialog
export const showAuditChangesDialog = (auditRecord: AuditChange, show: boolean = true) => ({
  type : AUDIT_ADMIN_DEVICE_CONFIG_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 * Invoked whenever user change the row per page dropdown
 */
export const setDeviceConfigGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});
